import {RsnBadge, RsnCarousel, RsnSkeleton} from '@raison/ui-kit';
import {useSuspenseQuery} from '@tanstack/react-query';
import React from 'react';
import {Link, LinkProps} from 'react-router';
import {useUserInfoQueryOptions} from '~/entities/user';
import {env} from '~/environment';
import scss from './HomePageBanner.module.scss';

type ModifierClasses<Name extends string, ScssStyles> = keyof {
    [Key in keyof ScssStyles as Key extends `${Name}--${infer M}` ? M : never]: any;
};

type Modifier<S extends string> = ModifierClasses<S, typeof scss>;

type HomePageBannerProps = {
    className?: string;
    variant: Modifier<'variant'>;
    title: string;
    details: string | null;
    href: string;
    badge: RsnBadge.Props;
};

export function HomePageBanner() {
    const {data: shouldHideOtp} = useSuspenseQuery({
        ...useUserInfoQueryOptions(),
        select: user => user.otpEnabled || user.status !== 'on_board',
    });

    const banners: HomePageBannerProps[] = [];
    if (!shouldHideOtp) {
        banners.push({
            title: 'Enhanced operations security',
            details: 'Confirm transactions via authenticator app',
            variant: 'authenticator',
            href: '/settings',
            badge: {color: 'white', decoration: 'arrow', children: 'Enable'},
        });
    }
    banners.push({
        title: 'Raison on Telegram',
        details: 'Market trends & investment opportunities',
        variant: 'telegram',
        href: 'https://t.me/rsn_finance',
        badge: {color: 'white', decoration: 'link', children: 'Open'},
    });

    return (
        <RsnCarousel slides={banners} dots='inside' theme='contrast' gaps='hack'>
            {slide => <Banner key={slide.key} className={slide.className} {...slide.data} />}
        </RsnCarousel>
    );
}

HomePageBanner.Skeleton = function HomePageBannerSkeleton() {
    return <RsnSkeleton height={112} borderRadius={16} />;
};

function isExternalHref(href: string) {
    if (href && URL.canParse(href)) {
        return new URL(href).hostname !== new URL(env.APP_URL).hostname;
    }
    return false;
}

function getBannerBackground(variant: Modifier<'variant'>) {
    switch (variant) {
        case 'authenticator':
            return new URL('./imgs/banner-1-original-2x.png', import.meta.url);
        case 'telegram':
            return new URL('./imgs/banner-2-original-2x.png', import.meta.url);
        default:
            return undefined;
    }
}

function Banner({variant, title, details, href, className, badge}: HomePageBannerProps) {
    const link: LinkProps = {to: href};
    if (isExternalHref(href)) {
        link.target = '_blank';
        link.rel = 'nofollow noreferrer noopener';
    }
    const losslessBackground = getBannerBackground(variant);
    return (
        <Link {...link} className={`${scss.banner} ${scss[`variant--${variant}`]} ${className}`}>
            <hgroup className='grid gap-y-1'>
                <h3 className={scss.textHigh}>{title}</h3>
                {!!details && <p className={scss.textLow}>{details}</p>}
            </hgroup>

            <RsnBadge.Md {...badge} />

            {losslessBackground && (
                <img className={scss.bannerBackground} src={losslessBackground.toString()} alt='' loading='lazy' />
            )}
        </Link>
    );
}
